// ============================================
// NOTIFICATIONS PAGE - i18n
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Bell,
  Check,
  Loader2,
  CheckCheck,
  Briefcase,
  Award,
  FileText,
  AlertTriangle,
  Star,
  Shield,
  Trash2,
  Building2,
} from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
  useUnfollowCompany,
  useFollowedCompanies,
} from '@/hooks';
import { formatRelativeTime, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/notifications')({
  component: NotificationsPage,
});

function NotificationsPage() {
  const { t } = useTranslation(['panel', 'common']);
  const { data: notificationsResponse, isLoading } = useNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();
  const unfollowMutation = useUnfollowCompany();
  const { data: followedResponse, isLoading: isFollowedLoading } = useFollowedCompanies();

  const notifications: any[] = (notificationsResponse as any)?.data || notificationsResponse || [];
  const followedCompanies: any[] = (followedResponse as any)?.data || followedResponse || [];
  const unreadCount = notifications.filter((n: any) => !n.isRead).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'application_received':
      case 'application_status_changed':
      case 'application_shortlisted':
      case 'application_rejected':
        return Briefcase;
      case 'job_match':
      case 'new_job_match':
      case 'company_job_posted':
        return Star;
      case 'certificate_expiring':
      case 'certificate_expired':
        return AlertTriangle;
      case 'badge_awarded':
        return Award;
      case 'verification_status':
        return Shield;
      case 'profile_view':
        return FileText;
      default:
        return Bell;
    }
  };

  const getNotificationColor = (type: string, isRead: boolean) => {
    if (isRead) return 'bg-gray-100 text-gray-500';
    
    switch (type) {
      case 'application_received':
      case 'application_status_changed':
      case 'application_shortlisted':
        return 'bg-blue-100 text-blue-600';
      case 'application_rejected':
        return 'bg-red-100 text-red-600';
      case 'job_match':
      case 'new_job_match':
      case 'company_job_posted':
        return 'bg-green-100 text-green-600';
      case 'certificate_expiring':
      case 'certificate_expired':
        return 'bg-orange-100 text-orange-600';
      case 'badge_awarded':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-maritime-100 text-maritime-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('notifications.title')}</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 
              ? t('notifications.unreadCount', { count: unreadCount }) 
              : t('notifications.allRead')}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsRead.mutate()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-maritime-600 bg-maritime-50 rounded-lg hover:bg-maritime-100 transition-colors disabled:opacity-50"
            disabled={markAllAsRead.isPending}
          >
            {markAllAsRead.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCheck className="w-4 h-4" />
            )}
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('notifications.empty.title')}</h3>
          <p className="text-gray-500">{t('notifications.empty.description')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {notifications.map((notification: any) => {
            const Icon = getNotificationIcon(notification.type);
            const colorClass = getNotificationColor(notification.type, notification.isRead);
            
            let parsedData: any = null;
            if (notification.data) {
              if (typeof notification.data === 'string') {
                try {
                  parsedData = JSON.parse(notification.data);
                } catch (e) {}
              } else if (typeof notification.data === 'object') {
                parsedData = notification.data;
              }
            }

            return (
              <div
                key={notification.id}
                className={cn(
                  'p-4 flex items-start gap-4 transition-colors',
                  !notification.isRead && 'bg-maritime-50/50'
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                    colorClass
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm',
                      !notification.isRead ? 'font-medium text-gray-900' : 'text-gray-600'
                    )}
                  >
                    {notification.title}
                  </p>
                  {notification.type === 'company_job_posted' ? (
                    <div className="mt-1">
                      <p className="text-sm text-gray-500">{notification.message}</p>
                      {parsedData?.jobTitle && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{parsedData.jobTitle}</p>
                            <p className="text-xs text-gray-500">{parsedData.companyName || 'Şirket'}</p>
                          </div>
                          {parsedData.jobSlug && (
                            <Link
                              to={`/jobs/${parsedData.jobSlug}`}
                              className="text-xs font-semibold text-maritime-600 hover:text-maritime-700 bg-maritime-50 hover:bg-maritime-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                            >
                              İlanı Gör →
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    notification.message && (
                      <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
                    )
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!notification.isRead && (
                    <button
                      onClick={() => markAsRead.mutate(notification.id)}
                      className="p-2 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-full transition-colors"
                      title={t('notifications.markRead')}
                      disabled={markAsRead.isPending}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification.mutate(notification.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title={t('common:delete')}
                    disabled={deleteNotification.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Takip Edilen Şirketler */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-maritime-600" />
          {t('notifications.followedCompanies')}
        </h2>

        {isFollowedLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-maritime-600" />
          </div>
        ) : followedCompanies.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">{t('notifications.followedCompaniesEmpty')}</h3>
            <p className="text-sm text-gray-500">{t('notifications.followedCompaniesEmptyDesc')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {followedCompanies.map((item: any) => {
              const company = item.company || item;
              return (
                <div key={item.id} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                    {company.logoUrl ? (
                      <img src={company.logoUrl} alt={company.name} className="w-full h-full object-cover rounded-maritime" />
                    ) : (
                      <Building2 className="w-5 h-5 text-maritime-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
                    {(company.city || company.country) && (
                      <p className="text-xs text-gray-500">{[company.city, company.country].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link
                      to={`/companies/${company.slug}`}
                      className="text-xs text-maritime-600 hover:text-maritime-700 font-medium"
                    >
                      {t('notifications.viewCompany')}
                    </Link>
                    <button
                      onClick={() => unfollowMutation.mutate(company.id)}
                      disabled={unfollowMutation.isPending}
                      className="text-xs text-red-500 hover:text-red-600 font-medium disabled:opacity-50"
                    >
                      {unfollowMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('companyDetail.unfollow')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
